import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useRouterActions } from 'react-resource-router';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { useFlags } from '@atlaskit/flag';
import SectionMessage from '@atlaskit/section-message';
import { LoadingSpinner } from '@atlassian/proforma-common-core/jira-common';
import { MediaProvider } from '@atlassian/proforma-common-core/jira-common-context';
import { Footer } from '../../common/ui/footer';
import { FormValidationMessage } from '../../common/ui/form-validation-message';
import { FormWithName } from '../../common/ui/form-with-name';
import { IssueFormsBody, IssueFormsBodyWrapper, IssueFormsContent } from '../../common/ui/styles';
import { copyToClipboard } from '../../common/utils/copy-to-clipboard';
import { getFormValidationResultMessage } from '../../common/utils/process-form-load-error';
import { useFormStoreController } from '../../controllers/form-store';
import { useAttachmentService } from '../../services/attachments';
import { useFormService } from '../../services/form';
import { useProjectsService } from '../../services/projects';
import { Header } from './header';
import { messages } from './messages';
export const IssueFormsGeneral = ({
  reporterAccountId
}) => {
  const {
    showFlag
  } = useFlags();
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const [{
    data: mediaApiUploadToken
  }, {
    loadMediaApiUploadToken
  }] = useAttachmentService();
  const [{
    data: form,
    error: formLoadError,
    loading: formLoading
  }, {
    load: loadForm,
    reset: resetForm
  }] = useFormService();
  const [{
    formStore
  }, {
    clear: clearFormStore,
    create: createFormStore
  }] = useFormStoreController(reporterAccountId);
  const intl = useIntl();
  const [{
    data: projects
  }] = useProjectsService();
  const {
    push
  } = useRouterActions();
  const [formDetails, setFormDetails] = useState();
  const [formValidationResultMessage, setFormValidationResultMessage] = useState();
  const selectedProject = useMemo(() => {
    if (formDetails === undefined || projects === undefined) {
      return undefined;
    }
    const formDetailsProjectIdString = formDetails.projectId.toString();
    return projects.find(project => project.id === formDetailsProjectIdString);
  }, [formDetails, projects]);
  useEffect(() => {
    setFormValidationResultMessage(undefined);
    if (formDetails) {
      loadForm(formDetails);
    } else {
      resetForm();
    }
  }, [formDetails, loadForm, resetForm]);
  useEffect(() => {
    if (formDetails !== null && formDetails !== void 0 && formDetails.projectId) {
      loadMediaApiUploadToken(formDetails.projectId);
    }
  }, [formDetails === null || formDetails === void 0 ? void 0 : formDetails.projectId, loadMediaApiUploadToken]);
  useEffect(() => {
    if (formDetails && form) {
      createFormStore(formDetails, form);
    } else {
      clearFormStore();
    }
  }, [clearFormStore, createFormStore, form, formDetails]);
  useEffect(() => {
    if (formLoadError && selectedProject && formDetails) {
      const newFormValidationResultMessage = getFormValidationResultMessage(formLoadError, selectedProject, formDetails, intl, push, showFlag, createAnalyticsEvent);
      if (newFormValidationResultMessage) {
        setFormValidationResultMessage(newFormValidationResultMessage);
      }
      resetForm();
    }
    // NOTE: If `formDetails` is included, then the 'form-load-error' flag will be redisplayed when moving from an erroring form.
    // NOTE: `intl` from `useIntl()` must not be used in a dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAnalyticsEvent, formLoadError, push, resetForm, selectedProject, showFlag]);
  let bodyAndFooter;
  if (formLoading) {
    bodyAndFooter = /*#__PURE__*/React.createElement(IssueFormsBodyWrapper, null, /*#__PURE__*/React.createElement(IssueFormsBody, null, /*#__PURE__*/React.createElement(LoadingSpinner, null)));
  } else if (formValidationResultMessage) {
    bodyAndFooter = /*#__PURE__*/React.createElement(IssueFormsBodyWrapper, null, /*#__PURE__*/React.createElement(IssueFormsBody, null, /*#__PURE__*/React.createElement(FormValidationMessage, {
      formValidationResultMessage: formValidationResultMessage
    })));
  } else if (formDetails && formStore && selectedProject) {
    const copyDirectLink = () => {
      let product;
      if (selectedProject.projectType === 'BUSINESS') {
        product = 'core';
      } else if (selectedProject.projectType === 'SERVICE_DESK') {
        product = 'servicedesk';
      } else if ( /* projectType === 'SOFTWARE' && */selectedProject.simplified) {
        product = 'software';
      } else {
        product = 'software/c';
      }
      const url = `${window.location.origin}/jira/${product}/projects/${selectedProject.key}/forms/form/direct/${formDetails.projectFormId}/${formDetails.issueType.id}`;
      copyToClipboard(formDetails.requestType ? `${url}?requestTypeId=${formDetails.requestType.id}` : url);
    };
    bodyAndFooter = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IssueFormsBodyWrapper, null, /*#__PURE__*/React.createElement(IssueFormsBody, null, /*#__PURE__*/React.createElement(SectionMessage, null, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.directLinkSectionMessageEmbed, {
      values: {
        a: label => /*#__PURE__*/React.createElement(Button, {
          appearance: "link",
          spacing: "none",
          onClick: copyDirectLink
        }, label)
      }
    }))), /*#__PURE__*/React.createElement(FormWithName, {
      formStore: formStore
    }))), /*#__PURE__*/React.createElement(Footer, {
      formDetails: formDetails,
      formStore: formStore,
      project: selectedProject,
      showCreateAnother: true
    }));
  }
  const mediaContext = {
    mediaApiUploadToken,
    projectId: formDetails === null || formDetails === void 0 ? void 0 : formDetails.projectId
  };
  return /*#__PURE__*/React.createElement(MediaProvider, {
    mediaContext: mediaContext
  }, /*#__PURE__*/React.createElement(IssueFormsContent, null, /*#__PURE__*/React.createElement(Header, {
    setFormDetails: setFormDetails
  }), bodyAndFooter));
};